import React, { useState, useEffect, useRef } from 'react';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Overview from './pages/Overview';
import Analytics from './pages/Analytics';
import Companies from './pages/Companies';
import UsersPage from './pages/UsersPage';
import CompanyViewer from './pages/CompanyViewer';
import Affiliates from './pages/Affiliates';
import LeadTracker from './pages/LeadTracker';
import CustomerReferrals from './pages/CustomerReferrals';
import Accounts from './pages/Accounts';
import Commissions from './pages/Commissions';
import Payouts from './pages/Payouts';
import Reports from './pages/Reports';
import LocalResourcesAdmin from './pages/LocalResourcesAdmin';
import StripeReconcile from './pages/StripeReconcile';
import TestAccounts from './pages/TestAccounts';
import AuditLog from './pages/AuditLog';
import Announcements from './pages/Announcements';
import SPIFs from './pages/SPIFs';
import Messages from './pages/Messages';
import Recruitment from './pages/Recruitment';
import SEU from './pages/SEU';
import { supabase, supabaseAuth } from './supabase';

// Admin users who can access the internal app (everything)
const ADMIN_EMAILS = ['josh@workvanapp.com', 'trent@workvanapp.com'];
// Recruiters can only access the recruitment + messages pages
const RECRUITER_EMAILS = ['tara@workvanapp.com'];

const ALLOWED_RECRUITER_PAGES = new Set(['recruitment', 'messages']);

function roleFor(emailLower) {
  if (ADMIN_EMAILS.includes(emailLower)) return 'admin';
  if (RECRUITER_EMAILS.includes(emailLower)) return 'recruiter';
  return null;
}

function App() {
  // Derive the active role from whatever email is in localStorage so recruiters
  // can't see admin pages even if they bookmark a deep URL.
  const initialEmail = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem('workvan_internal_auth') || 'null');
      if (saved && Date.now() - saved.timestamp < 30 * 24 * 60 * 60 * 1000) return saved.email;
    } catch {}
    return null;
  })();
  const initialRole = initialEmail ? roleFor(initialEmail) : null;
  const [currentPage, setCurrentPage] = useState(initialRole === 'recruiter' ? 'recruitment' : 'overview');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage for existing session
    const saved = localStorage.getItem('workvan_internal_auth');
    if (saved) {
      try {
        const { email, timestamp } = JSON.parse(saved);
        // Session valid for 30 days
        if (Date.now() - timestamp < 30 * 24 * 60 * 60 * 1000) {
          return email;
        }
      } catch (e) {}
    }
    return null;
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Admin-only preview state — declared up here with the other hooks so it
  // runs on every render, NOT after the splash/login conditional returns
  // (Rules of Hooks). Defaulted from sessionStorage; only takes effect for
  // admins via the userRole derivation below.
  const [previewAsRecruiter, setPreviewAsRecruiter] = useState(() =>
    sessionStorage.getItem('wv_preview_as') === 'recruiter'
  );

  // Splash screen — once per session
  const [showSplash, setShowSplash] = useState(() => {
    if (sessionStorage.getItem('wv_splash_shown')) return false;
    return true;
  });
  const [splashFading, setSplashFading] = useState(false);
  const splashVideoRef = useRef(null);

  useEffect(() => {
    if (!showSplash) return;
    sessionStorage.setItem('wv_splash_shown', 'true');
    // Fallback: dismiss after 4s even if video doesn't fire onEnded
    const fallback = setTimeout(() => {
      setSplashFading(true);
      setTimeout(() => setShowSplash(false), 600);
    }, 4000);
    return () => clearTimeout(fallback);
  }, [showSplash]);

  const handleSplashEnd = () => {
    setSplashFading(true);
    setTimeout(() => setShowSplash(false), 600);
  };
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Admin or recruiter — anything else denied
    const role = roleFor(email.toLowerCase());
    if (!role) {
      setError('Access denied. This email is not authorized.');
      setLoading(false);
      return;
    }

    try {
      // Use Supabase auth client (anon key) to verify credentials
      const { data, error: authError } = await supabaseAuth.auth.signInWithPassword({
        email,
        password
      });

      if (authError) throw authError;

      // Save to localStorage
      localStorage.setItem('workvan_internal_auth', JSON.stringify({
        email: email.toLowerCase(),
        timestamp: Date.now()
      }));

      setIsAuthenticated(email.toLowerCase());
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('workvan_internal_auth');
    try {
      await supabaseAuth.auth.signOut();
    } catch (e) {
      // Ignore signout errors
    }
    setIsAuthenticated(null);
    // Reset page state
    setCurrentPage('overview');
    setSelectedCompanyId(null);
  };

  const navigateToCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    setCurrentPage('company-viewer');
  };

  const handleSearchNavigate = (type, item) => {
    switch (type) {
      case 'company':
        navigateToCompany(item.id);
        break;
      case 'user':
        setCurrentPage('users');
        break;
      case 'affiliate':
        setCurrentPage('affiliates');
        break;
      default:
        break;
    }
  };

  // Splash screen
  if (showSplash) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: splashFading ? 0 : 1,
        transition: 'opacity 0.6s ease-out'
      }}>
        <video
          ref={splashVideoRef}
          src="/logo-animation.mp4"
          autoPlay
          muted
          playsInline
          onEnded={handleSplashEnd}
          style={{
            width: '100vw',
            height: '100vh',
            objectFit: 'contain'
          }}
        />
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '3rem',
          maxWidth: '400px',
          width: '100%',
          border: '1px solid #3a3a3a'
        }}>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            Work Van Internal
          </h1>
          <p style={{
            color: '#888',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            Admin Portal
          </p>

          <form onSubmit={handleLogin}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              required
              style={{
                width: '100%',
                padding: '1rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              style={{
                width: '100%',
                padding: '1rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />
            {error && (
              <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem',
                background: loading ? '#666' : '#ff6b35',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Signing in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const actualRole = isAuthenticated ? roleFor(isAuthenticated) : null;

  const togglePreviewRole = (on) => {
    if (actualRole !== 'admin') return;
    if (on) sessionStorage.setItem('wv_preview_as', 'recruiter');
    else sessionStorage.removeItem('wv_preview_as');
    setPreviewAsRecruiter(on);
    // Recruiters land on /recruitment. Exiting preview drops back to overview.
    setCurrentPage(on ? 'recruitment' : 'overview');
  };

  // Only honor preview state when the underlying account is actually an
  // admin — protects against a recruiter somehow getting the sessionStorage
  // flag (which would be a no-op for them anyway, but belt-and-suspenders).
  const userRole = (previewAsRecruiter && actualRole === 'admin') ? 'recruiter' : actualRole;

  // Render current page — recruiters can only see recruitment + messages.
  // Coerce any other page to recruitment so deep-linking + stale state can't
  // surface the admin views.
  const renderPage = () => {
    const effectivePage = userRole === 'recruiter' && !ALLOWED_RECRUITER_PAGES.has(currentPage)
      ? 'recruitment'
      : currentPage;
    switch (effectivePage) {
      case 'overview':
        return <Overview navigateToCompany={navigateToCompany} />;
      case 'analytics':
        return <Analytics />;
      case 'companies':
        return <Companies navigateToCompany={navigateToCompany} />;
      case 'users':
        return <UsersPage navigateToCompany={navigateToCompany} />;
      case 'company-viewer':
        return (
          <CompanyViewer
            companyId={selectedCompanyId}
            onBack={() => setCurrentPage('companies')}
          />
        );
      case 'dashboard':
        return <Dashboard />;
      case 'affiliates':
        return <Affiliates adminEmail={isAuthenticated} />;
      case 'leads':
        return <LeadTracker />;
      case 'customer-referrals':
        return <CustomerReferrals />;
      case 'accounts':
        return <Accounts />;
      case 'commissions':
        return <Commissions />;
      case 'payouts':
        return <Payouts />;
      case 'reports':
        return <Reports />;
      case 'local-resources':
        return <LocalResourcesAdmin />;
      case 'stripe-reconcile':
        return <StripeReconcile />;
      case 'test-accounts':
        return <TestAccounts adminEmail={isAuthenticated} />;
      case 'audit-log':
        return <AuditLog />;
      case 'announcements':
        return <Announcements />;
      case 'spifs':
        return <SPIFs />;
      case 'messages':
        return <Messages />;
      case 'recruitment':
        return <Recruitment />;
      case 'seu':
        return <SEU adminEmail={isAuthenticated} />;
      default:
        return <Overview navigateToCompany={navigateToCompany} />;
    }
  };

  return (
    <Layout
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      userEmail={isAuthenticated}
      userRole={userRole}
      actualRole={actualRole}
      previewAsRecruiter={previewAsRecruiter}
      onTogglePreviewRole={togglePreviewRole}
      onLogout={handleLogout}
      onSearchNavigate={handleSearchNavigate}
    >
      {renderPage()}
    </Layout>
  );
}

export default App;
